/* `/zadania` — the screen's own chrome, the index and the desk.
 *
 * Canon: `docs/design/zadania-2026-09-18/zadania-c-runda5.html`, direction C "Fokus", round 3. The
 * measures that file states and this one keeps: **index 320 wide**, **card 836 centred with air on
 * both sides**, the index ending on the card's bottom edge, the two decision buttons standing ALONE
 * under the card at the card's width (owner: do not move them into the card), and the keyboard
 * legend in the page foot on the line of the muted sentence.
 *
 * Widths and heights are layout rather than design tokens (stylelint.config.js says so); every
 * colour, space, radius, shadow, duration and type value below is a token. */

.page {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

/* ── The head ──────────────────────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: flex-end;
}

.title {
  /* The mock sets 30/800/-.035em/1.05. `--fs-h1` is 28 and the nearest the type scale has — a
   * screen title is 26 or 28 in this app and the mock's 30 has no token, so the larger of the two
   * is what it takes. `--ls-display` (-0.03em) is the nearest tracking. */
  font-size: var(--fs-h1);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-display);
  line-height: var(--lh-tight);
  color: var(--text);
}

/* No focus ring on it, and that is the house rule rather than an omission: `AppShell` gives the
 * `h1` `tabindex="-1"` and focuses it on every route change so the screen announces itself, and a
 * ring drawn for a move the trainer did not make is a box appearing around the title on arrival.
 * `:focus-visible` is the browser's heuristic and Chromium does draw one here. Every screen in this
 * app that is focused this way suppresses it the same way. */
.title:focus {
  outline: none;
}

/* The count line. It states a figure that arrives with a read, so it holds its own line box from
 * the first frame — an empty `<p>` collapses and the grid under it starts 19px higher.
 *
 * `--space-6px` of air above it, which is the canon's own `.head .sub` margin. At 1.5px the two ran
 * into each other: `--lh-tight` is under 1, so the title's line box is SHORTER than its glyphs and
 * a near-zero margin puts the sub line inside the tail of the "Z". */
.sub {
  min-height: 19px;
  margin-top: var(--space-6px);

  /* The mock's 13px. `--fs-foot` is that exactly. */
  font-size: var(--fs-foot);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.headRight {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  margin-left: auto;
}

/* The cog. `position: relative` for the ember dot, which is the one accent in the head. */
.cog {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 34px;
  height: 34px;
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

/* The focus ring on this screen is INK and follows the control's own radius.
 *
 * The owner saw an EMBER RECTANGLE standing around the pill of "Gotowe". Two causes, both here:
 * the ring was `--accent`, and it was drawn on `.act`, which has no radius of its own, so the
 * outline squared off the corners of the `--radius-xl` pill it sits inside. Drawing it on the row
 * that owns the pill fixes the shape; `--text` fixes the colour, and matches the ring the owner
 * settled on for the exercise library on 18 September. Ember stays a scalpel — here it marks one
 * thing only, the muted-kind dot on the cog.
 *
 * `:focus-visible`, never `:focus`: the ring belongs to the keyboard. It does appear after a mouse
 * click followed by a KEY (the Z-to-undo flow) — that is Chromium re-arming the heuristic on the
 * keypress, and it is correct behaviour, not the defect that was reported. */
.cog:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-sm);
}

.cog:hover {
  color: var(--text);
  background: var(--bg-inset);
}

.cogIcon {
  width: 17px;
  height: 17px;
}

/* **One of the two ember marks on this screen.** It says a kind is muted, which is a real state the
 * trainer can act on; the ring around it is the page's own ground so the dot reads as a dot rather
 * than as a smudge on the button's edge. No blur — the coloured-glow ban is absolute. */
.cogDot {
  position: absolute;
  top: var(--space-minus-2px);
  right: var(--space-minus-2px);
  width: 9px;
  height: 9px;
  background: var(--accent);
  border: var(--space-2px) solid var(--bg);
  border-radius: var(--r-circle);
}

/* ── The two columns ───────────────────────────────────────────────────────────────────────── */

.grid {
  display: grid;
  flex: 1;
  grid-template-columns: 320px minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
  gap: var(--space-6) var(--space-4);
  min-height: 0;
  margin-top: var(--space-5);
}

/* ── The index ends on the CARD's bottom edge, not on the decisions' ────────────────────────
 * Canon, measured: index top == card top AND index bottom == CARD bottom, with the two decision
 * buttons standing alone below the card and nothing beside them on the left. Two grid ROWS is what
 * states that, rather than a `calc()` against the decision bar's height: the index occupies the
 * first row only, and the row's own height is whatever the card takes. */
.queue {
  grid-row: 1;
  grid-column: 1;
}

/* The desk. The card centres in whatever room is left, at its own 836; the decision bar and the
 * deck edge share that width so the three read as one object. */
.desk {
  grid-row: 1;
  grid-column: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  min-height: 0;
}

/* Nothing is waiting, so there is no decision bar under the card and the reward takes both rows. */
.deskCleared {
  grid-row: 1 / span 2;
  justify-content: center;
}

/* The card's stack: the card itself, and two faint plates behind it saying there are more.
 *
 * SUBTLE, and that is a recorded owner decision of 18 September 2026: a louder version was drawn
 * (outlined, two offsets, a 44px gap) and rejected the same day. Do not strengthen it. */
.stack {
  position: relative;

  /* ONE grid cell, and every card in it.
   *
   * It was a flex row, which is the same thing while exactly one card is mounted — and during a
   * swap there are two, so the answered card and the one replacing it stood SIDE BY SIDE at half
   * width each. A single cell with both children in it is what lets the outgoing card leave across
   * the incoming one without either being laid out again. */
  display: grid;
  flex: 1;
  grid-template-rows: minmax(0, 1fr);
  grid-template-columns: minmax(0, 1fr);
  width: 836px;
  max-width: 100%;
  min-height: 0;
}

/* One card's place on the deck. Both the card leaving and the card arriving take it. */
.plate {
  z-index: 1;
  display: flex;
  grid-area: 1 / 1;
  min-width: 0;
  min-height: 0;
}

/* BEHIND the card, and that is what `z-index` is doing here rather than decoration: a pseudo-element
 * of a positioned ancestor paints ABOVE a static child, so both plates were drawn over the card's
 * own action bar — two rounded outlines crossing the buttons. The card is `z-index: 1` with its own
 * opaque ground, so all the plates show is the 8 and 14 pixels below its bottom edge. */
.stack::before,
.stack::after {
  z-index: 0;
  position: absolute;
  right: 20px;
  left: 20px;
  height: 40px;
  content: '';
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl);
}

.stack::before {
  bottom: var(--space-minus-8px);
  opacity: 0.7;
}

.stack::after {
  right: 40px;
  bottom: var(--space-minus-14px);
  left: 40px;
  opacity: 0.4;
}

/* The second grid row, in the desk's column only — so nothing stands to the left of them, which is
 * what the canon draws and what the owner called clean. */
.decide {
  grid-row: 2;
  grid-column: 2;
  justify-self: center;
  width: 836px;
  max-width: 100%;
}

/* ── The foot ──────────────────────────────────────────────────────────────────────────────── */

/* The muted sentence on the left, the keyboard legend right-aligned on the same line — the canon's
 * own arrangement, so nothing hangs below the queue. It holds its height in every state: the
 * sentence is only drawn when a kind is muted, and a foot that appeared when the read landed would
 * move the grid above it. */
.foot {
  display: flex;
  flex: none;
  gap: var(--space-5);
  align-items: center;
  justify-content: space-between;
  min-height: 20px;
  margin-top: var(--space-3);
}

.muted {
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.legend {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  margin-left: auto;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.legendItem {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  white-space: nowrap;
}

.key {
  display: inline-grid;
  place-items: center;
  min-width: 18px;
  height: 18px;
  padding: var(--space-0) var(--space-5px);
  font-size: var(--fs-tag);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-5px);
}

/* ── The three short states ────────────────────────────────────────────────────────────────── */

/* The whole two-column grid's place, so a pending, failed or empty screen is the same box as a full
 * one and nothing moves when the answer lands. */
.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  margin-top: var(--space-5);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl);
}

.retry {
  margin-top: var(--space-4);
}
